/**
 * liPai 类型策略：立牌（外容器矩形 + 内插口矩形）。
 *
 * 固定 bottom 贴底边（spec §3.2）：
 *   - 外容器（移动范围）仅在 addComponent / 无缓存 placement 时 initContainer 一次，之后冻结；
 *   - 内插口仅在外容器内左右拖；刀线刷新不重算外容器。
 *
 * 刀线刷新由 orchestrator 统一接管（add / touchend slot 变化 / remove）。
 *
 * 导出（spec §7）：
 *   socketInfoList:        [{ point, width, height, slotRect, componentId }]
 *   originSocketInfoList:  [{ point, width, height, containerPoint, containerWidth,
 *                              containerHeight, baseCanvasWidth, ... }]
 */

import { DRAW_TYPE, DEFAULT_CONFIG, lipaiColor } from '../constants.js';
import { registerComponentType } from '../typeRegistry.js';
import {
  convertCanvasToBackend,
  getRestoreBusinessScale,
  rectCanvasToSlotRect
} from '../core.js';

/**
 * 校验立牌 geometryConfig。
 * @param {Object} g - geometryConfig
 * @returns {string|null} 错误文案；null 表示通过
 */
function validateGeometryConfig(g) {
  if (!g) return '缺少组件尺寸配置：geometryConfig';
  if (typeof g.slotWidthMm !== 'number' || g.slotWidthMm <= 0) {
    return '缺少或非法字段：slotWidthMm（必须 > 0 的数字）';
  }
  if (typeof g.slotHeightMm !== 'number' || g.slotHeightMm <= 0) {
    return '缺少或非法字段：slotHeightMm（必须 > 0 的数字）';
  }
  if (
    g.slotRange != null &&
    (typeof g.slotRange !== 'number' || g.slotRange <= 0 || g.slotRange > 1)
  ) {
    return 'slotRange 必须在 (0, 1] 范围内';
  }
  return null;
}

/**
 * 取轮廓极值点（与 serviceHoleNew#getLowestPoint 一致：多并列时取靠近另一轴中线的点）。
 * @param {Array<Array<number>>} points - canvas 坐标 [[x,y],...]
 * @param {0|1} axis - 0=x、1=y
 * @param {boolean} isMax - 取最大还是最小
 * @returns {{x:number,y:number}}
 */
function getExtremePoint(points, axis, isMax) {
  let extreme = isMax ? -Infinity : Infinity;
  let collected = [];
  for (const p of points) {
    const v = p[axis];
    const better = isMax ? v > extreme : v < extreme;
    if (better) {
      extreme = v;
      collected = [p];
    } else if (v === extreme) {
      collected.push(p);
    }
  }
  if (!collected.length) return { x: 0, y: 0 };
  if (collected.length === 1) return { x: collected[0][0], y: collected[0][1] };
  const other = axis === 0 ? 1 : 0;
  const vals = collected.map((p) => p[other]);
  const mid = (Math.min.apply(null, vals) + Math.max.apply(null, vals)) / 2;
  let best = collected[0];
  let minD = Infinity;
  for (let i = 0; i < collected.length; i++) {
    const p = collected[i];
    const d = Math.abs(p[other] - mid);
    if (d < minD) {
      minD = d;
      best = p;
    }
  }
  return { x: best[0], y: best[1] };
}

/**
 * @param {number} n
 * @param {number} lo
 * @param {number} hi
 * @returns {number}
 */
function clamp(n, lo, hi) {
  if (lo > hi) return (lo + hi) / 2;
  return Math.max(lo, Math.min(hi, n));
}

/**
 * 计算内插口宽高（canvas px）。
 * @param {Object} instance
 * @param {Object} sharedState
 * @returns {{ slotWidthPx: number, slotHeightPx: number }}
 */
function getSlotSizePx(instance, sharedState) {
  const g = instance.geometryConfig;
  const m2p = sharedState.mmToPixelRatio / (sharedState.maskFitScale || 1);
  return {
    slotWidthPx: g.slotWidthMm * m2p,
    slotHeightPx: g.slotHeightMm * m2p
  };
}

/**
 * placement 相对当前画布的缩放系数。
 * @param {Object} o - originSocketInfo 片段
 * @param {Object} sharedState
 * @returns {number}
 */
function getPlacementScale(o, sharedState) {
  // 按去除留白后的业务坐标宽度缩放，避免 padding 变化导致还原位置偏移。
  if (o && o.baseCanvasWidth && o.baseCanvasWidth !== sharedState.canvasWidth) {
    return getRestoreBusinessScale(o, sharedState);
  }
  return 1;
}

/**
 * 按添加时刻 outline 初始化外容器（bottom 贴底）；写入 instance.containerRect。
 * @param {Object} instance
 * @param {Object} sharedState
 * @returns {void}
 */
function initContainer(instance, sharedState) {
  const g = instance.geometryConfig;
  const defaultSlotRange = DEFAULT_CONFIG.liPaiSlotRangePercent / 100;
  const slotRange = g.slotRange != null ? g.slotRange : defaultSlotRange;
  const sizes = getSlotSizePx(instance, sharedState);
  const slotWidthPx = sizes.slotWidthPx;
  const slotHeightPx = sizes.slotHeightPx;
  const points = sharedState.outlinePoints;

  // 立牌插口可移动范围 = outline轮廓最左最右点距离 × 比例
  const outlineMinX = Math.min(
    ...points.map(function (p) {
      return p[0];
    })
  );
  const outlineMaxX = Math.max(
    ...points.map(function (p) {
      return p[0];
    })
  );
  const outlineWidth = outlineMaxX - outlineMinX;
  let containerW = outlineWidth * slotRange;
  if (containerW > sharedState.drawImageWidth) {
    containerW = sharedState.drawImageWidth;
  }
  // 最小不低于立牌自身宽度，防止极端窄轮廓导致插口无法移动
  if (containerW < slotWidthPx) containerW = slotWidthPx;

  const anchorPt = getExtremePoint(points, 1, true);
  // 容器以轮廓水平中点居中，而非最底部点的 x
  let containerCenterX = (outlineMinX + outlineMaxX) / 2;
  let containerLeft = containerCenterX - containerW / 2;
  let containerRight = containerCenterX + containerW / 2;

  if (containerLeft < sharedState.imageLeft) {
    containerLeft = sharedState.imageLeft;
    containerCenterX = sharedState.imageLeft + containerW / 2;
    containerRight = containerCenterX + containerW / 2;
  }
  if (containerRight > sharedState.imageRight) {
    containerRight = sharedState.imageRight;
    containerCenterX = sharedState.imageRight - containerW / 2;
    containerLeft = containerCenterX - containerW / 2;
  }

  instance.containerRect = {
    x: containerLeft,
    y: anchorPt.y,
    w: containerW,
    h: slotHeightPx
  };
}

/**
 * 在已冻结的 containerRect 内更新内插口（需 sharedState 算尺寸）。
 * @param {Object} instance
 * @param {Object} sharedState
 * @param {number|null|undefined} slotCenterX
 * @returns {void}
 */
function updateSlotInContainerWithState(instance, sharedState, slotCenterX) {
  const c = instance.containerRect;
  if (!c) return;
  const sizes = getSlotSizePx(instance, sharedState);
  const slotWidthPx = sizes.slotWidthPx;
  const slotHeightPx = sizes.slotHeightPx;

  const cxLo = c.x + slotWidthPx / 2;
  const cxHi = c.x + c.w - slotWidthPx / 2;
  let slotCx =
    slotCenterX != null && !isNaN(slotCenterX) ? slotCenterX : c.x + c.w / 2;
  slotCx = clamp(slotCx, cxLo, cxHi);

  instance.slotRect = {
    x: slotCx - slotWidthPx / 2,
    y: c.y,
    w: slotWidthPx,
    h: slotHeightPx
  };
  instance.slotCenter = {
    x: slotCx,
    y: c.y + slotHeightPx / 2
  };
  instance.dragAxis = 'x';
}

/**
 * init：还原或首次初始化外容器 + 内插口。
 * @param {Object} instance
 * @param {Object} sharedState
 * @param {Object} options
 * @param {Object} [options.placement] - { originSocketInfo }
 * @returns {void}
 */
function init(instance, sharedState, options) {
  const placement = options && options.placement;
  const o = placement && placement.originSocketInfo;
  let slotCenterX = null;

  if (
    o &&
    o.containerPoint &&
    typeof o.containerWidth === 'number' &&
    typeof o.containerHeight === 'number'
  ) {
    const k = getPlacementScale(o, sharedState);
    instance.containerRect = {
      x: o.containerPoint.x * k,
      y: o.containerPoint.y * k,
      w: o.containerWidth * k,
      h: o.containerHeight * k
    };
  } else {
    initContainer(instance, sharedState);
  }

  if (
    o &&
    o.point &&
    typeof o.width === 'number' &&
    typeof o.height === 'number'
  ) {
    const k = getPlacementScale(o, sharedState);
    slotCenterX = (o.point.x + o.width / 2) * k;
  }

  updateSlotInContainerWithState(instance, sharedState, slotCenterX);
}

/**
 * clampSelf：仅重夹内插口，不重算外容器。
 * @param {Object} instance
 * @param {Object} sharedState
 * @returns {boolean} 恒 false（立牌不通过 clampSelf 报 outline 违规）
 */
function clampSelf(instance, sharedState) {
  if (instance.slotCenter) {
    updateSlotInContainerWithState(
      instance,
      sharedState,
      instance.slotCenter.x
    );
  }
  return false;
}

/**
 * 命中检测：外容器内任意位置可命中。
 * @param {Object} instance
 * @param {{x:number,y:number}} point
 * @returns {boolean}
 */
function hitTest(instance, point) {
  const c = instance.containerRect;
  if (!c) return false;
  return (
    point.x >= c.x &&
    point.x <= c.x + c.w &&
    point.y >= c.y &&
    point.y <= c.y + c.h
  );
}

/**
 * 拖动起点：记录插口中心与指针，避免从容器空白起拖瞬移。
 * @param {Object} instance
 * @param {{x:number,y:number}} point
 * @returns {void}
 */
function onDragStart(instance, point) {
  instance._dragRef = {
    px: point.x,
    cx: instance.slotCenter.x
  };
}

/**
 * 拖动：仅更新内插口 centerX。
 * @param {Object} instance
 * @param {{x:number,y:number}} point
 * @param {Object} sharedState
 * @returns {void}
 */
function onDragMove(instance, point, sharedState) {
  const ref = instance._dragRef;
  if (!ref) return;
  const nextX = ref.cx + (point.x - ref.px);
  updateSlotInContainerWithState(instance, sharedState, nextX);
}

/**
 * @param {Object} instance
 * @returns {void}
 */
function onDragEnd(instance) {
  instance._dragRef = null;
}

/**
 * 编辑模式：外容器虚线 + 内插口实色。
 * @param {Object} instance
 * @param {CanvasRenderingContext2D} ctx
 * @param {Object} sharedState
 * @returns {void}
 */
function drawEdit(instance, ctx, sharedState) {
  const c = instance.containerRect;
  const s = instance.slotRect;
  if (!c || !s) return;
  ctx.save();

  ctx.setLineDash([2, 2]);
  ctx.strokeStyle = lipaiColor;
  ctx.fillStyle = 'rgba(64, 123, 255, 0.10)';
  ctx.lineWidth = Math.max(sharedState.lineWidth, 1);
  ctx.beginPath();
  ctx.rect(c.x, c.y, c.w, c.h);
  ctx.fill();
  ctx.stroke();

  ctx.setLineDash([]);
  ctx.fillStyle = lipaiColor + '33';
  ctx.beginPath();
  ctx.rect(s.x, s.y, s.w, s.h);
  ctx.fill();
  ctx.stroke();

  ctx.restore();
}

/**
 * 预览模式（效果图）：不绘制 container / slot；槽口形状由共享 outlinePoints 刀线体现（spec §4.4）。
 * @param {Object} instance
 * @param {CanvasRenderingContext2D} ctx
 * @param {Object} sharedState
 * @returns {void}
 */
function drawPreview(/* instance, ctx, sharedState */) {
  return;
}

/**
 * @param {Object} instance
 * @returns {Object}
 */
function getCollisionShape(instance) {
  const s = instance.slotRect;
  return { kind: 'rect', x: s.x, y: s.y, w: s.w, h: s.h };
}

/**
 * @param {Object} instance
 * @returns {{x:number,y:number,w:number,h:number}}
 */
function getSelectionRect(instance) {
  const c = instance.containerRect;
  return { x: c.x, y: c.y, w: c.w, h: c.h };
}

/**
 * @param {Object} instance
 * @returns {{x:number,y:number}}
 */
function getMagnifierAnchor(instance) {
  return { x: instance.slotCenter.x, y: instance.slotCenter.y };
}

/**
 * 导出 layerData 片段。
 * @param {Object} instance
 * @param {Object} sharedState
 * @returns {Object}
 */
function toExport(instance, sharedState) {
  const s = instance.slotRect;
  const c = instance.containerRect;
  const beTopLeft = convertCanvasToBackend({ x: s.x, y: s.y }, sharedState);
  const beWidth = Math.round(s.w / sharedState.canvasScale);
  const beHeight = Math.round(s.h / sharedState.canvasScale);
  const slotRect = rectCanvasToSlotRect(s, sharedState);
  const socketInfoItem = {
    point: beTopLeft,
    width: beWidth,
    height: beHeight,
    slotRect,
    componentId: instance.id
  };
  const originItem = {
    point: { x: s.x, y: s.y },
    width: s.w,
    height: s.h,
    containerPoint: { x: c.x, y: c.y },
    containerWidth: c.w,
    containerHeight: c.h,
    baseCanvasWidth: sharedState.canvasWidth,
    canvasRoundWhiteSpace: sharedState.canvasRoundWhiteSpace,
    componentId: instance.id,
    geometryConfig: instance.geometryConfig,
    sizePresetId: instance.sizePresetId || '',
    enableMagnifier: instance.enableMagnifier !== false
  };
  return {
    socketInfoList: [socketInfoItem],
    originSocketInfoList: [originItem]
  };
}

/**
 * dirty 比较：外容器 + 内插口位置。
 * @param {Object} instance
 * @returns {Object}
 */
function serializeForDirty(instance) {
  const s = instance.slotRect;
  const c = instance.containerRect;
  return {
    cx: Math.round(c.x * 100) / 100,
    cy: Math.round(c.y * 100) / 100,
    cw: Math.round(c.w * 100) / 100,
    ch: Math.round(c.h * 100) / 100,
    sx: Math.round(s.x * 100) / 100,
    sy: Math.round(s.y * 100) / 100
  };
}

/**
 * 供 orchestrator 调 imageBorderParse 的 slotRect。
 * @param {Object} instance
 * @param {Object} sharedState
 * @returns {Array<number>}
 */
function getSlotRectForApi(instance, sharedState) {
  const slotRect = rectCanvasToSlotRect(instance.slotRect, sharedState);
  const g = instance.geometryConfig || {};
  // imageBorderParse 需要当前插口物理规格，避免 api.js 回退到模板默认 slotWidth/slotHeight。
  if (typeof g.slotWidthMm === 'number' && typeof g.slotHeightMm === 'number') {
    slotRect[4] = g.slotWidthMm;
    slotRect[5] = g.slotHeightMm;
  }
  return slotRect;
}

const liPaiStrategy = {
  type: DRAW_TYPE.LI_PAI,
  validateGeometryConfig,
  init,
  clampSelf,
  hitTest,
  onDragStart,
  onDragMove,
  onDragEnd,
  drawEdit,
  drawPreview,
  getCollisionShape,
  getSelectionRect,
  getMagnifierAnchor,
  toExport,
  serializeForDirty,
  getSlotRectForApi
};

registerComponentType(liPaiStrategy);

export default liPaiStrategy;
